import styled from 'styled-components'
import IconClio from '../../../images/Clio.png'
import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { CheckReqParams, ConsolidateArgs, CreateItem, FetchItem, FetchOptions } from '../../../utils/appdata'
import { InitialCaps } from '../../../utils'
import classNames from 'classnames'
import { Icon, Label } from '../../../components'
import { areEqual, VariableSizeList as List } from 'react-window'
import { Modal, Tooltip } from 'antd'
import AutoSizer from 'react-virtualized-auto-sizer'
import InfiniteLoader from 'react-window-infinite-loader'
import deepEqual from 'fast-deep-equal'
import uuid from 'uuid'

const RowBlock = styled.div`
  color: ${props => props.theme.colorTable.backLightText};
  background: ${props => props.theme.colorTable.backLight};

  &:hover {
    color: ${props => props.theme.colorTable.backLightText};
    background: ${(props) => props.theme.colorTable.backLightHover} !important;
  }

  &.active {
    color: ${props => props.theme.colorTable.listSelectionText} !important;
    background: ${(props) => props.theme.colorTable.listSelection} !important;
  }
`
const getEntityLabel = (typeName, typeDefs) => {
  const typeDef = typeDefs[typeName]
  let typeLabel = typeDef && typeDef.label && typeDef.label.trim() // TODO: DEPRECATE typeDef.label (#749)
  if (!typeLabel) {
    const cPos = typeName.indexOf(':')
    typeLabel = (cPos >= 0) ? typeName.substr(cPos + 1) : typeName
  }
  return InitialCaps(typeLabel)
}

const getTitle = (providerName, typeName, typeDefs) => {
  if (providerName === 'Clio') {
    return (<h3 className={'m-0'}>Select <img className={'pb-1'} alt={'ClioIcon'} src={IconClio}/> {
      getEntityLabel(typeName, typeDefs)
    }</h3>)
  } else {
    return <h3 className={'m-0'}>Select an item below</h3> // {getEntityLabel(typeName, typeDefs)}</h3>
  }
}

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(
    () => {
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)
      return () => {
        clearTimeout(handler)
      }
    },
    [value, delay],
  )
  return debouncedValue
}

const SelectForm = (props) => {
  if (!props) return null
  const {
    def,
    context,
    queryStatus,
    autoShow,
    hideButton,
    spaceLeft,
    spaceRight,
  } = props
  console.log('SelectForm')
  const [visible, setVisible] = useState(false)
  const [manuallyClosed, setManuallyClosed] = useState(false)
  const [search, setSearch] = useState('')
  const [hasNextPage, setHasNextPage] = useState(true)
  const [isNextPageLoading, setIsNextPageLoading] = useState(false)
  const [items, setItems] = useState([])
  const [isAddNew, setAddNew] = useState(false)
  const [args, setArgs] = useState(undefined)
  const debouncedSearch = useDebounce(search, 200)

  const globalContext = context.global
  const isLink = !!queryStatus
  const canModify = isLink && queryStatus.canModify
  const canAdd = isLink && canModify // todo: maybe add a separate permission/setting for queries to control wheteher to allow add?
  const linkedValue = isLink && queryStatus.hasLink && queryStatus.linkValue
  const hasLinkedValue = !!linkedValue
  const keyName = context && context.linkedKeyName && context.linkedKeyName !== '_N_A_'
    ? context.linkedKeyName
    : def.keyName

  const newArgs = def.args ? ConsolidateArgs(def, def.args.map(argStr => context.evaluateExpression(argStr))) : []
  if (!deepEqual(args, newArgs)) {
    setArgs(newArgs)
  }
  const getNameReqParam = () => {
    const reqParams = def.params.filter(param => param.req).map(param => param.name)
    if (reqParams.length) {
      if (!args) {
        return reqParams[0]
      }
      reqParams.forEach(param => {
        if (!(param in args)) {
          return param
        }
      })
    }
  }

  function capitalizeFirstLetter (str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  const fetchQuery = async (nextPage = false) => {
    if (!CheckReqParams(def, args)) {
      return
    }
    if (nextPage) {
      setIsNextPageLoading(true)
    }
    // need the list of fields from the query
    let sumFields = context.getVarNamesSummary(def.typeName)
    const data = await FetchOptions(def, sumFields, args, globalContext, nextPage && items.length, debouncedSearch)
    if (linkedValue && keyName) { // initialize __isActive on fetched items
      const linkedItem = data.find(item => item[keyName] === linkedValue)
      if (linkedItem) {
        linkedItem.__isActive = true
      }
    }
    if (nextPage) {
      setItems([...items, ...data])
    } else {
      setItems(data)
    }
    setHasNextPage(data.length === 20)
    if (nextPage) {
      setIsNextPageLoading(false)
    }
  }

  useEffect(
    () => {
      if (args) { // avoid executing query if any required arguments were missing
        fetchQuery()
      }
    },
    [debouncedSearch, args],
  )

  const showModal = useCallback(
    () => {
      setVisible(true)
    },
    [visible],
  )

  const hideModal = useCallback(
    () => {
      setVisible(false)
      setManuallyClosed(true)
    },
    [visible],
  )

  const changeSearch = useCallback(
    (val) => {
      setSearch(val)
    },
    [search],
  )

  const silentRefreshLink = useCallback(
    () => {
      fetchAndSetValue(linkedValue)
    },
    [linkedValue],
  )

  const linkButtonEnabled = true

  const buttonLabel = (
    isLink
      ? hasLinkedValue
        ? `Refresh from ${def.label}`
        : `Link to ${def.label}`
      : `Select from ${def.label}`
  )
  const buttonClasses = classNames('btn btn-outline-secondary dart-btn-outline',
    { 'mr-3': spaceRight, 'ml-3': spaceLeft })
  const SelectButton = useMemo(
    () => CheckReqParams(def, args)
      ? (
        <button className={buttonClasses}
                style={{ backgroundColor: '#FFFFFF' }}
                disabled={!linkButtonEnabled}
                onClick={(canModify && hasLinkedValue) ? silentRefreshLink : showModal}>{buttonLabel}</button>
      )
      : (
        <Tooltip key={uuid()}
                 title={'This command requires additional data that has not yet been entered: ' +
                 capitalizeFirstLetter(getNameReqParam())}
                 placement={'bottom'}
                 destroyTooltipOnHide={true}
                 overlayClassName={'warning-tooltip'}>
          <span className="d-inline-block" tabIndex="0" style={{ cursor: 'not-allowed' }}>
            <button className={buttonClasses}
                    style={{
                      backgroundColor: '#FFFFFF',
                      'pointer-events': 'none',
                    }}
                    disabled={true}
                    onClick={showModal}>{buttonLabel}</button>
          </span>
        </Tooltip>
      ),
    [def, args, showModal],
  )

  const SearchInput = useMemo(
    () => (
      <div className={'mb-2'}>
        <label htmlFor={'FilterByKeyword'}>Filter by keyword</label>
        <input id={'FilterByKeyword'}
               type={'text'}
               value={search}
               onChange={event => changeSearch(event.target.value)}
               className={'form-control rounded-2'}
               autoComplete="off"
        />
      </div>
    ),
    [search],
  )

  const loadMoreItems = () => {}
  const isItemLoaded = index => {
    const isLoaded = !hasNextPage || index < items.length
    if (!isLoaded) {
      if (!isNextPageLoading) {
        if (args) { // avoid executing query if any required arguments were missing
          fetchQuery(true)
        }
      }
    }
    return isLoaded
  }

  const changeActive = useCallback((index) => {
    setItems(items.map((item, _index) => {
      if (index === _index) {
        item.__isActive = !item.__isActive
      } else {
        delete item.__isActive
      }
      return item
    }))
    if (index === -1) {
      setAddNew(true)
    } else if (isAddNew) {
      setAddNew(false)
    }
  }, [items])

  const getKeyName = () => {
    let key = context.typeDef.key || def.keyName || 'id$'
    if (key === '_id' && !context.typeDef.key && def.provider === 'Knackly') { // keyNameHack
      key = 'id$'
    }
    return key
  }

  const selectActiveItem = async () => {
    let nextValue = isAddNew ? null : items.find(item => item.__isActive)
    // null is magic value meaning "create new" -- different from undefined!
    if (nextValue || nextValue === null) {
      const key = getKeyName()
      if (nextValue) {
        const { __isActive, ...newValue } = nextValue
        nextValue = newValue
        const itemId = nextValue[key]
        if (itemId) {
          await fetchAndSetValue(itemId, key)
        }
      } else if (nextValue === null) { // create new!
        // nextValue only === null when the "create new" item has been selected in the UI.
        // This is only the case for "link to", never for "select from".
        nextValue = await CreateItem(def, key, context, context.instanceId)
        if (nextValue) {
          if (def.mapTo) {
            context.linkMappedObjectValue(nextValue, def.typeName, key, def.mapTo)
          } else {
            context.linkSimpleObjectValue(nextValue, def.typeName, key)
          }
        }
      }
      hideModal()
    }
  }

  const fetchAndSetValue = async (itemId, keyName = undefined) => {
    if (itemId) {
      const nextValue = await FetchItem(def, itemId, keyName, context, context.instanceId, true)
      if (nextValue && Object.keys(nextValue).length) {
        if (isLink) { // link to...
          if (!keyName) keyName = getKeyName()
          if (def.mapTo) {
            context.linkMappedObjectValue(nextValue, def.typeName, keyName, def.mapTo)
          } else {
            context.linkSimpleObjectValue(nextValue, def.typeName, keyName)
          }
        } else { // select from...
          if (def.mapTo) {
            context.copyMappedObjectValue(nextValue, def.typeName, def.mapTo)
          } else {
            context.copySimpleObjectValue(nextValue)
          }
        }
      } else {
        console.error('Query returned invalid object after selection')
      }
    }
  }

  const itemCount = hasNextPage ? items.length + 1 : items.length

  const renderBodyRow = (obj) => {
    const c = globalContext.getPushedContext(obj, def.typeName)
    return <span className={'mr-5 ml-2'}>{c.getSummary() || `(${def.typeName})`}</span>
  }

  // eslint-disable-next-line react/display-name
  const Row = memo(
    ({
       index,
       style,
     }) => {
      const loading = !isItemLoaded(index)

      if (loading) {
        return (<div style={style} className={'py-1'}>
          <RowBlock className={classNames('rounded rounded-2 p-2 knackly-back-light')}>
            <span className={'mx-5'}>Loading...</span>
          </RowBlock>
        </div>)
      }

      const rowItem = items[index]
      const isActive = rowItem.__isActive || false

      return (
        <div style={style} key={rowItem[keyName]} className={'py-1'}>
          <RowBlock
            className={classNames('rounded rounded-2 p-2 cursorPointer', { active: isActive })}
            onClick={() => changeActive(index)}>
            <span><Icon type={isActive ? 'icon-check-circle' : 'icon-circle'}
                        className={classNames('h5 m-0')} style={{ verticalAlign: 0 }}/>
            </span>
            {renderBodyRow(rowItem)}
          </RowBlock>
        </div>
      )
    },
    areEqual,
  )

  if (autoShow && !visible && !manuallyClosed && items.length) {
    setVisible(true)
  }

  return (
    <Fragment>
      {hideButton ? null : SelectButton}
      {CheckReqParams(def, args) && (
        <Modal
          title={getTitle(def.provider, def.typeName, context.typeDefs)}
          className={'app-modal'}
          visible={visible}
          onCancel={hideModal}
          maskClosable={false}
          destroyOnClose={true}
          footer={(<div className={'d-flex'}>
            {canAdd && (
              /* Add a "create new item" radio button as an alternative to the above listed items */
              <div className="form-check ml-2">
                <input type="checkbox" id={'CreateNew' + def.label} name={'CreateNew' + def.label}
                       className="form-check-input"
                       checked={isAddNew}
                       onClick={() => changeActive(isAddNew ? -2 : -1)}
                />
                <Label name={def.label}
                       label={`Create a new ${getEntityLabel(def.typeName, context.typeDefs)} in ${def.label}`}
                       forId={'CreateNew' + def.label}
                       className={'form-check-label'}/>
              </div>
            )}
            <button key="OK" className={'btn btn-secondary dart-btn ml-auto'} onClick={selectActiveItem}>SELECT</button>
          </div>)}>
          <div className={'w-100 overflow-auto'} style={{
            minHeight: 200,
            maxHeight: '60vh',
          }}>
            {SearchInput}

            <div style={{ height: '48ch' }}>
              <AutoSizer>
                {({
                    height,
                    width,
                  }) => (
                  <InfiniteLoader
                    isItemLoaded={isItemLoaded}
                    itemCount={itemCount}
                    loadMoreItems={loadMoreItems}
                  >
                    {(onItemsRendered, ref) => (
                      <List
                        style={{ overflowX: 'hidden' }}
                        height={height}
                        width={width}
                        onItemsRendered={onItemsRendered}
                        ref={ref}
                        itemCount={itemCount}
                        itemSize={() => 45}>
                        {Row}
                      </List>
                    )}
                  </InfiniteLoader>
                )}
              </AutoSizer>
            </div>
          </div>
        </Modal>)}
    </Fragment>
  )
}

export default SelectForm
